import { useEffect, useRef } from 'react';

/**
 * Ejecuta `fn` cada `activeMs` cuando la pestaña está visible, y cada
 * `hiddenMs` cuando está oculta. Reduce carga al backend cuando el
 * usuario no está mirando.
 *
 * fn: () => void | Promise<void>
 * activeMs: ms entre polls con pestaña visible (default 20s)
 * hiddenMs: ms entre polls con pestaña oculta (default 60s)
 * enabled: si es false, no hace nada
 */
export function useVisibilityPolling(fn, activeMs = 20000, hiddenMs = 60000, enabled = true) {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    if (!enabled) return;
    let timer = null;

    const currentInterval = () =>
      document.visibilityState === 'visible' ? activeMs : hiddenMs;

    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(async () => {
        try { await fnRef.current(); } catch { /* silent */ }
        schedule();
      }, currentInterval());
    };

    const onVisibility = () => schedule(); // reprograma con el nuevo intervalo

    schedule();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [activeMs, hiddenMs, enabled]);
}
